/** Photo pipeline (PRD 10): crop and resize on device, upload full (1024) and thumb (256) JPEGs. */

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Default framing: horizontally centred, and vertically around 30% from the top (where faces usually are). */
export function defaultCropPercent(w: number, h: number): CropArea {
  if (w <= h) {
    const height = (100 * w) / h;
    const y = Math.min(Math.max(30 - height / 2, 0), 100 - height);
    return { x: 0, y, width: 100, height };
  }
  const width = (100 * h) / w;
  return { x: (100 - width) / 2, y: 0, width, height: 100 };
}

/** Longest side of the working copy the cropper uses. Phone photos are 12–48MP, which is slow and glitchy to pan and zoom. */
export const WORKING_MAX = 2048;

/** Size scaled down (never up) so the longest side is at most `max`. */
export function fitWithin(w: number, h: number, max: number = WORKING_MAX): { w: number; h: number } {
  const k = Math.min(1, max / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/**
 * Turn whatever the person picked into a plain, upright, reasonably sized JPEG. The rotation stored in the file
 * (EXIF) is applied to the pixels and then dropped, so the cropper and the final crop always see the same picture,
 * whatever the browser does with orientation or very large images.
 */
export async function normalize(file: Blob): Promise<{ blob: Blob; w: number; h: number }> {
  const src = await decode(file);
  try {
    const { w: sw, h: sh } = size(src);
    if (!sw || !sh) throw new Error('empty image');
    const { w, h } = fitWithin(sw, sh);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(src, 0, 0, w, h);
    return { blob: await toJpeg(canvas), w, h };
  } finally {
    if ('close' in src) src.close();
  }
}

export async function decode(src: Blob): Promise<ImageBitmap | HTMLImageElement> {
  const url = URL.createObjectURL(src);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } catch {
    return await createImageBitmap(src, { imageOrientation: 'from-image' });
  } finally {
    URL.revokeObjectURL(url);
  }
}

const size = (s: ImageBitmap | HTMLImageElement) =>
  s instanceof HTMLImageElement ? { w: s.naturalWidth, h: s.naturalHeight } : { w: s.width, h: s.height };

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.86));
}

async function render(src: ImageBitmap | HTMLImageElement, area: CropArea, px: number): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, px, px);
  ctx.drawImage(src, area.x, area.y, area.width, area.height, 0, 0, px, px);
  return toJpeg(canvas);
}

export interface EncodedPhoto {
  full: Blob;
  thumb: Blob;
  previewUrl: string;
}

/** `area` is in source pixels (react-easy-crop's croppedAreaPixels). Omit to centre-crop. */
export async function encodePhoto(file: Blob, area?: CropArea): Promise<EncodedPhoto> {
  const src = await decode(file);
  const { w, h } = size(src);
  const crop = area ?? (() => {
    const s = Math.min(w, h);
    return { x: (w - s) / 2, y: 0, width: s, height: s };
  })();
  const [full, thumb] = await Promise.all([render(src, crop, 1024), render(src, crop, 256)]);
  if ('close' in src) src.close();
  return { full, thumb, previewUrl: URL.createObjectURL(thumb) };
}

export async function dataUrlToBlob(url: string): Promise<Blob> {
  return (await fetch(url)).blob();
}
